import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from "react";
import {
  DealDocumentRowContainer,
  DealDocumentRowIconContainer,
  DealDocumentRowLeftContainer,
  DealDocumentRowRightContainer,
} from "./DealDocumentRowItem";
import DealDocumentListIcon from "../dealDocumentListIcon";
import { Link } from "react-router-dom";
import { isEmpty } from "../../../../helpers/utilsTS";
import EditIcon from "@mui/icons-material/Edit";
import DKFormTextInput from "../../../common/widgets/formVariablesWidget/DKFormTextInput";
import { updateDealDocument } from "../../../../api/deals/documents";
import { Cancel, CheckCircle } from "@mui/icons-material";

type DealDocumentRowContainerProps = {
  orgId: number;
  dealId: number;
  dealDocumentId: number;
  documentName: string;
  documentLinkPath?: string;
  documentNameAdornment?: ReactElement | ReactElement[];
  key?: string;
  highlightRow?: () => void;
  editMode: boolean;
  setEditMode: (editModeValue: boolean) => void;
};
export const DealDocumentRow = (
  props: PropsWithChildren<DealDocumentRowContainerProps>
) => {
  const {
    orgId,
    dealId,
    dealDocumentId,
    documentName,
    documentLinkPath,
    documentNameAdornment,
    children,
    key,
    highlightRow,
    editMode,
    setEditMode,
  } = props;

  const documentNameAdornments =
    documentNameAdornment && Array.isArray(documentNameAdornment)
      ? documentNameAdornment
      : [documentNameAdornment];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(
    `(max-width:${theme.breakpoints.values.lg}px)`
  );

  const [displayEditButton, setDisplayEditButton] = useState<boolean>(false);
  const [nameFieldValue, setNameFieldValue] = useState<string>(documentName);

  useEffect(() => {
    setNameFieldValue(documentName);
  }, [documentName]);

  const handleSaveNameField = () => {
    setEditMode(false);
    setDisplayEditButton(false);
    if (highlightRow) {
      highlightRow();
    }
    updateDealDocument(
      orgId,
      dealId,
      dealDocumentId,
      { name: nameFieldValue },
      (savedDealDocument) => {
        setNameFieldValue(savedDealDocument.name);
      }
    );
  };

  const handleCancelNameField = () => {
    setEditMode(false);
    setDisplayEditButton(false);
    setNameFieldValue(documentName);
  };

  return (
    <DealDocumentRowContainer key={key}>
      <DealDocumentRowLeftContainer>
        <Box
          sx={{
            marginBottom: isSmallScreen ? "10px" : "0px",
            display: "flex",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <DealDocumentRowIconContainer>
              <DealDocumentListIcon />
            </DealDocumentRowIconContainer>
            {editMode && (
              <Box
                marginRight={2}
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
              >
                <Box minWidth={"350px"} marginRight={2}>
                  <DKFormTextInput
                    dataTestId={"document-name-field"}
                    label={""}
                    size={"compact"}
                    name={"dealDocumentNameField"}
                    value={nameFieldValue}
                    onChange={(event) => {
                      setNameFieldValue(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSaveNameField();
                      } else if (event.key === "Escape") {
                        handleCancelNameField();
                      }
                    }}
                  />
                </Box>
                <Box display={"flex"} gap={1} flexDirection={"row"}>
                  <CheckCircle
                    data-testid={"document-name-save-button"}
                    onClick={() => {
                      handleSaveNameField();
                    }}
                    sx={{ cursor: "pointer" }}
                    color={"secondary"}
                  />
                  <Cancel
                    onClick={() => {
                      handleCancelNameField();
                    }}
                    sx={{ cursor: "pointer" }}
                    htmlColor={theme.palette.tertiary.main}
                  />
                </Box>
              </Box>
            )}
            {!isEmpty(documentLinkPath) && !editMode && (
              <Box
                marginRight={2}
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                onMouseOver={() => {
                  setDisplayEditButton(true);
                }}
                onMouseOut={() => {
                  setDisplayEditButton(false);
                }}
                onMouseLeave={() => {
                  setDisplayEditButton(false);
                }}
              >
                <Link
                  data-testid="document-name"
                  to={documentLinkPath}
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <Typography variant="link" style={{ paddingRight: "10px" }}>
                    {nameFieldValue}
                  </Typography>
                </Link>
                {displayEditButton && (
                  <EditIcon
                    onClick={() => {
                      setEditMode(true);
                    }}
                    sx={{ cursor: "pointer" }}
                    htmlColor={theme.palette.secondary.dark}
                  />
                )}
                {!displayEditButton && (
                  <Box display={"block"} height={24} width={24} />
                )}
              </Box>
            )}
            {isEmpty(documentLinkPath) && !editMode && (
              <Typography variant="body1" style={{ paddingRight: "10px" }}>
                {nameFieldValue}
              </Typography>
            )}
            {!isSmallScreen &&
              documentNameAdornments &&
              documentNameAdornments.map((adornment) => {
                return adornment;
              })}
          </Box>
        </Box>
      </DealDocumentRowLeftContainer>
      <DealDocumentRowRightContainer>{children}</DealDocumentRowRightContainer>
    </DealDocumentRowContainer>
  );
};
